import "./list-employee-loan.scss";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetEmployeeLoan } from "@core/query/employee-loan.query";
import { GetEmployeeLoanModel } from "@core/model/query-model-employee-loan";
import { TableSort } from "@core/model/table-sort";
import { FilterEmployeeLoan } from "../filter-employee-loan/filter-employee-loan";
import { useFilterEmployeeLoanContext } from "../filter-employee-loan/filter-employee-loan.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { Tag } from "primereact/tag";
import { getColor } from "@core/utility/get-color";

export interface ListEmployeeLoanProps {
  isMain?: boolean;
}
export function ListEmployeeLoan({ isMain }: ListEmployeeLoanProps) {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { formatDate } = useDateTimeContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterEmployeeLoanContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: employeeLoanResponse,
    isLoading,
    isError,
    error,
  } = useQyGetEmployeeLoan(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetEmployeeLoanModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
      </div>

      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterEmployeeLoan />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{employeeLoanResponse?.count}</b>
      </h5>

      <DataTable
        value={employeeLoanResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          body={(cell) => (
            <div className="font-bold">
              {cell.person_first_name} {cell.person_last_name}
            </div>
          )}
          header="Employee"
          sortable
        ></Column>
        <Column
          body={(cell) => currencyFormat(cell.amount, "PHP")}
          header="Amount"
          sortable
        ></Column>
        <Column
          body={(cell) => formatDate(cell.effectivity_date)}
          header="Effectivity Date"
          sortable
        ></Column>
        <Column field="subject" header="Subject" sortable></Column>
        <Column field="purpose" header="Purpose" sortable></Column>
        <Column
          body={(cell) => (
            <Tag
              className={getColor(cell.status_name)?.bgClass}
              value={cell.status_name}
            ></Tag>
          )}
          header="Status"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={employeeLoanResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="EmployeeLoan" className="employee-loan">
      <HeaderContent title="Employee Loan">
        {!isMain ? (
          <span className="flex gap-2">
            <Button label="New" onClick={handleNew} />
          </span>
        ) : null}
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListEmployeeLoan;
